import { useMemo, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

type TagBoxProps<T> = {
  source: T[];
  value?: string | null;
  getLabel?: (item: T) => string;
  getValue?: (item: T) => string | number;
  onChange: (value: string) => void;
  className?: string;
  placeholder?: string;
};

export function TagBox<T>({ source, value, onChange, getLabel, getValue, className = "", placeholder = "Select tags" }: TagBoxProps<T>) {

  const normalizeComparableValue = (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) return "";

    // Keep value comparisons stable across numeric string variants like "1" and "1.0".
    const numeric = Number(trimmed);
    if (!Number.isNaN(numeric) && String(numeric) === trimmed.replace(/\.0+$/, "")) {
      return String(numeric);
    }

    return trimmed;
  };

  const parseSelectedValues = (raw: string | null | undefined) => {
    if (!raw) return [] as string[];

    const input = String(raw).trim();
    if (!input) return [] as string[];

    if (input.startsWith("[") && input.endsWith("]")) {
      try {
        const parsed = JSON.parse(input);
        if (Array.isArray(parsed)) {
          return parsed
            .map((v) => String(v ?? "").trim())
            .filter((v) => v.length > 0);
        }
      } catch {
        // Continue with delimiter split as fallback.
      }
    }

    return input
      .split(/[;,]/)
      .map((v) => v.trim())
      .filter((v) => v.length > 0);
  };

  const options = useMemo(() => {
    return (source ?? []).map((item) => ({
      label: getLabel ? getLabel(item) : String((item as { label?: unknown }).label ?? ""),
      value: String(getValue ? getValue(item) : ((item as { value?: unknown }).value ?? "")),
    }));
  }, [source, getLabel, getValue]);

  const selectedValues = useMemo(() => {
    return parseSelectedValues(value);
  }, [value]);

  const selectedTags = useMemo(() => {
    return selectedValues.map((selectedValue) => {
      const selectedComparable = normalizeComparableValue(selectedValue);
      const match = options.find((option) => {
        if (option.value === selectedValue) return true;
        return normalizeComparableValue(option.value) === selectedComparable;
      });
      return {
        value: selectedValue,
        label: match?.label ?? selectedValue,
      };
    });
  }, [selectedValues, options]);

  const selectedComparableSet = useMemo(() => {
    return new Set(selectedValues.map((v) => normalizeComparableValue(v)));
  }, [selectedValues]);

  const availableOptions = useMemo(() => {
    return options.filter((option) => !selectedComparableSet.has(normalizeComparableValue(option.value)));
  }, [options, selectedComparableSet]);

  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (!containerRef.current?.contains(e.relatedTarget as Node)) {
      setOpen(false);
    }
  };

  const toggleTag = (tagValue: string) => {
    const isSelected = selectedValues.includes(tagValue);
    const next = isSelected
      ? selectedValues.filter((t) => t !== tagValue)
      : [...selectedValues, tagValue];
    onChange(next.join(","));
  };

  return (
    <div ref={containerRef} className={`relative ${className}`} onBlur={handleBlur}>
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex h-8 w-full items-center justify-between rounded-md border border-input bg-transparent px-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      >
        <span className="flex flex-wrap gap-1 flex-1 min-w-0">
          {selectedTags.length === 0 ? (
            <span className="text-muted-foreground">{placeholder}</span>
          ) : (
            selectedTags.map((tag) => (
              <span
                key={tag.value}
                className="inline-flex items-center rounded-full bg-muted border border-border px-2 py-0.5 text-xs font-medium"
                onClick={(e) => { e.stopPropagation(); toggleTag(tag.value); }}
              >
                {tag.label}<span className="ml-1 text-[10px]">×</span>
              </span>
            ))
          )}
        </span>
        <ChevronDown className={`ml-2 h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {/* Dropdown list */}
      {(open ) && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-popover shadow-md">
          <div className="flex flex-wrap grid-cols-2 gap-1 p-2 max-h-60 overflow-y-auto">
            {availableOptions.length === 0 ? (
              <div className="col-span-2 px-2 py-1 text-sm text-muted-foreground">
                All items selected
              </div>
            ) : (
              availableOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); toggleTag(option.value); }}
                  className="rounded-md border border-border bg-background px-2 py-1 text-left text-sm text-foreground hover:border-primary/70"
                >
                  {option.label}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
